import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api } from "../api";
import { useAuth } from "../auth";
import { initials } from "../Layout";
import ConfirmTypeModal from "../components/ConfirmTypeModal";
import PaySplitPanel from "../components/PaySplitPanel";
import { CURRENCY_SYMBOLS } from "../currency";

const fmtDate = (d) =>
  new Date(d).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });

const fmtNum = (v) => {
  const n = Number(v);
  if (!isFinite(n)) return "0";
  return Number.isInteger(n) ? n.toString() : n.toFixed(2);
};

// Pull a date out of "Label - DD-MM-YYYY" or "DD-MM-YYYY" (optionally followed
// by a "-N" collision suffix). Returns a Date or null.
const extractSheetDate = (name) => {
  const m = name?.match(/(\d{1,2})-(\d{1,2})-(\d{4})(?:-\d+)?\s*$/);
  if (!m) return null;
  const d = new Date(Number(m[3]), Number(m[2]) - 1, Number(m[1]));
  return isNaN(d.getTime()) ? null : d;
};

// Sort: newest date first; sheets without a parseable date go last.
const byExtractedDateDesc = (a, b) => {
  const da = extractSheetDate(a.name);
  const db = extractSheetDate(b.name);
  if (!da && !db) return 0;
  if (!da) return 1;
  if (!db) return -1;
  return db - da;
};

function GoogleSheetsIcon({ size = 18 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true">
      <path
        fill="#0F9D58"
        d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6z"
      />
      <path fill="#0A7E40" d="M14 2l6 6h-6z" />
      <path fill="#F1F1F1" d="M8 11.5h8v7.5H8z" />
      <g stroke="#0F9D58" strokeWidth="0.9">
        <line x1="8" y1="14" x2="16" y2="14" />
        <line x1="8" y1="16.5" x2="16" y2="16.5" />
        <line x1="12" y1="11.5" x2="12" y2="19" />
      </g>
    </svg>
  );
}

function SheetRow({ sheet, user, navigate, deleteSheet }) {
  const isAdminOrClient = user?.role === "admin" || user?.role === "client";
  const isEmployee = user?.role === "employee";
  const showTotal =
    isAdminOrClient && sheet.total !== null && sheet.total !== undefined;
  const showMyPay =
    isEmployee && sheet.my_pay !== null && sheet.my_pay !== undefined;
  const symbol = CURRENCY_SYMBOLS[sheet.currency] || "$";
  return (
    <div
      className="row clickable"
      role="link"
      tabIndex={0}
      onClick={() => navigate(`/sheets/${sheet.id}`)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          navigate(`/sheets/${sheet.id}`);
        }
      }}
    >
      <div className="lead">
        <span
          className="info-icon"
          data-tip={`Created ${fmtDate(sheet.created_at)}`}
          onClick={(e) => e.stopPropagation()}
          aria-label={`Created ${fmtDate(sheet.created_at)}`}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <circle cx="12" cy="12" r="9.5" stroke="currentColor" strokeWidth="1.6" />
            <circle cx="12" cy="7.5" r="1.2" fill="currentColor" />
            <path d="M12 11v6.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
          </svg>
        </span>
        <span className="title">{sheet.name}</span>
        <span className={`paid-pill${sheet.paid ? "" : " not-paid"}`}>
          {sheet.paid ? "Paid" : "Not paid"}
        </span>
        {user?.role === "admin" && sheet.pending_inbound > 0 && (
          <span
            className="owe-pill"
            title="The client changed this milestone's Google tab. Until you approve or reject those changes, this page won't sync out, so nothing they typed gets overwritten."
          >
            {sheet.pending_inbound} client edit{sheet.pending_inbound === 1 ? "" : "s"} to review
          </span>
        )}
        {user?.role === "admin" && sheet.employee_unpaid_amount != null && (
          <span
            className="owe-pill"
            title="The client has paid for this milestone, but an employee who earned money on it hasn't been marked paid yet"
          >
            Owe employee {symbol}{fmtNum(sheet.employee_unpaid_amount)}
          </span>
        )}
      </div>
      <div className="row-h">
        {user?.role === "admin" && sheet.google_spreadsheet_id && (
          <a
            className="sheet-link"
            href={
              `https://docs.google.com/spreadsheets/d/${sheet.google_spreadsheet_id}/edit` +
              // The project has one spreadsheet; land on this page's own tab.
              (sheet.google_tab_id != null ? `#gid=${sheet.google_tab_id}` : "")
            }
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            title="Open this page's tab in the project's Google Sheet"
          >
            <GoogleSheetsIcon size={18} />
            <span>Open sheet</span>
          </a>
        )}
        {showTotal && (
          <span className="sheet-total">
            {symbol}{fmtNum(sheet.total)}
          </span>
        )}
        {showMyPay && (
          <span className="sheet-total" title="Your pay for this milestone">
            {symbol}{fmtNum(sheet.my_pay)}
          </span>
        )}
        {user?.role === "admin" && !sheet.paid && (
          <button className="danger" onClick={(e) => deleteSheet(e, sheet.id)}>Delete</button>
        )}
      </div>
    </div>
  );
}

function ToPayBadge({ projectId }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get(`/projects/${projectId}/pay-split`)
      .then((r) => setData(r.data))
      .catch(() => setData(null));
  }, [projectId]);
  if (!data || !data.buckets || data.buckets.length === 0) return null;
  const parts = data.buckets
    .filter((b) => Number(b.to_employees_outstanding) > 0)
    .map((b) => `${CURRENCY_SYMBOLS[b.currency] || "$"}${fmtNum(b.to_employees_outstanding)}`);
  if (parts.length === 0) return null;
  return (
    <span className="topay-badge">
      <span className="badge-dot" />
      <span className="badge-label">To pay</span>
      <span className="badge-value">{parts.join(" + ")}</span>
    </span>
  );
}

function SheetSections({ sheets, user, navigate, deleteSheet, showPast, setShowPast }) {
  const active = sheets.filter((s) => !s.paid).sort(byExtractedDateDesc);
  const past = sheets.filter((s) => s.paid).sort(byExtractedDateDesc);
  // Paid milestones (the only ones this can ever apply to) sit collapsed by
  // default, so surface the warning on the toggle itself -- otherwise it's
  // invisible until someone happens to expand the section.
  const unpaidPastCount = past.filter((s) => s.employee_unpaid_amount != null).length;
  return (
    <>
      {active.length > 0 && (
        <>
          <div className="list">
            {active.map((s) => (
              <SheetRow key={s.id} sheet={s} user={user} navigate={navigate} deleteSheet={deleteSheet} />
            ))}
          </div>
        </>
      )}
      {past.length > 0 && (
        <div style={{ marginTop: active.length > 0 ? 20 : 0 }}>
          <div className="row-h" style={{ gap: 8 }}>
            <button
              type="button"
              className="ghost"
              onClick={() => setShowPast((v) => !v)}
              style={{ padding: "6px 12px", fontSize: "0.85rem" }}
            >
              {showPast
                ? "▾ Hide past milestones"
                : `▸ Show past milestones (${past.length})`}
            </button>
            {!showPast && user?.role === "admin" && unpaidPastCount > 0 && (
              <span
                className="owe-pill"
                title="These paid milestones still have an employee you haven't marked paid"
              >
                {unpaidPastCount} unpaid to employees
              </span>
            )}
          </div>
          {showPast && (
            <div className="list paid-list" style={{ marginTop: 10 }}>
              {past.map((s) => (
                <SheetRow key={s.id} sheet={s} user={user} navigate={navigate} deleteSheet={deleteSheet} />
              ))}
            </div>
          )}
        </div>
      )}
    </>
  );
}

export default function ProjectDetail() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [project, setProject] = useState(null);
  const [sheets, setSheets] = useState([]);
  const [newName, setNewName] = useState("");
  const [editing, setEditing] = useState(false);
  const [clients, setClients] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [editForm, setEditForm] = useState({ name: "", client_id: "", members: {} });
  const [deleteSheetId, setDeleteSheetId] = useState(null);
  const [showPast, setShowPast] = useState(false);
  const [googleConnected, setGoogleConnected] = useState(false);
  const [syncingAll, setSyncingAll] = useState(false);
  const [syncAllMsg, setSyncAllMsg] = useState("");

  const load = async () => {
    const [p, s] = await Promise.all([
      api.get(`/projects/${projectId}`),
      api.get(`/projects/${projectId}/sheets`),
    ]);
    setProject(p.data);
    setSheets(s.data);
    // members map: user_id -> { included: bool, percentage: string }
    const members = {};
    for (const m of (p.data.memberships || [])) {
      members[m.user_id] = {
        included: true,
        percentage: m.default_percentage != null ? String(m.default_percentage) : "",
      };
    }
    setEditForm({
      name: p.data.name,
      client_id: p.data.client_id || "",
      members,
    });
  };

  useEffect(() => {
    load();
  }, [projectId]);

  useEffect(() => {
    if (user?.role === "admin") {
      api.get("/users", { params: { role: "client" } }).then((r) => setClients(r.data));
      api.get("/users", { params: { role: "employee" } }).then((r) => setEmployees(r.data));
      api
        .get("/google/status")
        .then((r) => setGoogleConnected(!!r.data?.connected))
        .catch(() => setGoogleConnected(false));
    }
  }, [user]);

  // Give every milestone its own tab in the project's spreadsheet. Past ones are
  // read-only, so nothing re-syncs them on its own; this is how they get a tab.
  // The syncs run in a background worker, so poll the list until the tabs land.
  const syncAllSheets = async () => {
    setSyncingAll(true);
    setSyncAllMsg("");
    try {
      const { data } = await api.post(`/projects/${projectId}/sheets/sync-all`);
      setSyncAllMsg(`Syncing ${data.queued} page${data.queued === 1 ? "" : "s"}…`);
      for (let i = 0; i < 15; i++) {
        await new Promise((r) => setTimeout(r, 2000));
        const { data: rows } = await api.get(`/projects/${projectId}/sheets`);
        setSheets(rows);
        if (rows.every((s) => s.google_tab_id !== null && s.google_tab_id !== undefined)) {
          setSyncAllMsg("All pages synced");
          break;
        }
        setSyncAllMsg(
          `Synced ${rows.filter((s) => s.google_tab_id != null).length} of ${rows.length}…`
        );
      }
    } catch (e) {
      setSyncAllMsg(e?.response?.data?.detail || "Sync failed");
    } finally {
      setSyncingAll(false);
      setTimeout(() => setSyncAllMsg(""), 5000);
    }
  };

  const todayStr = (() => {
    const now = new Date();
    return `${String(now.getDate()).padStart(2, "0")}-${String(now.getMonth() + 1).padStart(2, "0")}-${now.getFullYear()}`;
  })();

  const nextAvailableName = (base) => {
    const existing = new Set(sheets.map((s) => s.name));
    if (!existing.has(base)) return base;
    let i = 1;
    while (existing.has(`${base}-${i}`)) i++;
    return `${base}-${i}`;
  };

  // If the typed label already ends with a date (DD-MM-YYYY or D-M-YYYY),
  // take the input as-is. Otherwise append today's date as a suffix.
  const HAS_DATE = /\d{1,2}-\d{1,2}-\d{4}\s*$/;

  const resolvedName = (() => {
    const label = newName.trim();
    if (!label) return nextAvailableName(todayStr);
    if (HAS_DATE.test(label)) return nextAvailableName(label);
    return nextAvailableName(`${label} - ${todayStr}`);
  })();

  const addSheet = async (e) => {
    e.preventDefault();
    await api.post(`/projects/${projectId}/sheets`, { name: resolvedName });
    setNewName("");
    load();
  };

  const requestDeleteSheet = (e, id) => {
    e.stopPropagation();
    setDeleteSheetId(id);
  };

  const confirmDeleteSheet = async () => {
    await api.delete(`/sheets/${deleteSheetId}`);
    setDeleteSheetId(null);
    load();
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    const employees = Object.entries(editForm.members)
      .filter(([, v]) => v.included)
      .map(([uid, v]) => ({
        user_id: Number(uid),
        default_percentage:
          v.percentage === "" || v.percentage === null ? null : Number(v.percentage),
      }));
    await api.patch(`/projects/${projectId}`, {
      name: editForm.name,
      client_id: editForm.client_id ? Number(editForm.client_id) : null,
      employees,
    });
    setEditing(false);
    load();
  };

  const toggleEmp = (id) => {
    setEditForm((f) => ({
      ...f,
      members: {
        ...f.members,
        [id]: {
          included: !(f.members[id]?.included),
          percentage: f.members[id]?.percentage || "",
        },
      },
    }));
  };

  const setEmpPct = (id, value) => {
    setEditForm((f) => ({
      ...f,
      members: {
        ...f.members,
        [id]: {
          included: f.members[id]?.included ?? false,
          percentage: value,
        },
      },
    }));
  };

  if (!project) return <div className="loading">Loading</div>;

  return (
    <>
      <div className="page-head">
        <div className="title-block">
          <div className="crumbs-row">
            <div className="crumbs">
              <Link to="/">Projects</Link>
              <span className="sep">/</span>
              <span>{project.name}</span>
            </div>
            {(user?.role === "admin" || user?.role === "client") && (() => {
              const outstanding = {};
              for (const s of sheets) {
                if (s.paid) continue;
                if (s.total === null || s.total === undefined) continue;
                outstanding[s.currency] = (outstanding[s.currency] || 0) + Number(s.total);
              }
              const entries = Object.entries(outstanding);
              const formatted = entries.length === 0
                ? "$0"
                : entries.map(([cur, sum]) => `${CURRENCY_SYMBOLS[cur] || "$"}${fmtNum(sum)}`).join(" + ");
              return (
                <span className="outstanding-badge" title="What clients still owe you">
                  <span className="badge-dot" />
                  <span className="badge-label">From client</span>
                  <span className="badge-value">{formatted}</span>
                </span>
              );
            })()}
            {user?.role === "admin" && <ToPayBadge projectId={projectId} />}
          </div>
          <div className="title-main">
            <h1>{project.name}</h1>
            {user?.role === "admin" && !editing && (
              <div className="project-meta">
                <div className="meta-item">
                  <span className="meta-label">Client</span>
                  {project.client ? (
                    <span className="member-chip client-chip">
                      <span className="avatar">{initials(project.client.name)}</span>
                      {project.client.name}
                    </span>
                  ) : (
                    <span className="muted-sm">—</span>
                  )}
                </div>
                <span className="meta-sep" />
                <div className="meta-item">
                  <span className="meta-label">Team</span>
                  {(project.employees || []).length === 0 ? (
                    <span className="muted-sm">No employees</span>
                  ) : (
                    <div className="member-strip">
                      {(project.employees || []).map((e) => (
                        <span key={e.id} className="member-chip">
                          <span className="avatar">{initials(e.name)}</span>
                          {e.name}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                <span className="meta-sep" />
                <div className="meta-item">
                  <span className="meta-label">Created</span>
                  <span className="meta-value">{fmtDate(project.created_at)}</span>
                </div>
              </div>
            )}
          </div>
        </div>
        {user?.role === "admin" && (
          <button onClick={() => setEditing((e) => !e)}>{editing ? "Cancel" : "Edit project"}</button>
        )}
      </div>

      {editing && (
        <form className="card pad" onSubmit={saveEdit} style={{ marginBottom: 16 }}>
          <div className="form-grid">
            <div>
              <label className="field">Project name</label>
              <input value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} />
            </div>
            <div>
              <label className="field">Client</label>
              <select
                value={editForm.client_id}
                onChange={(e) => setEditForm({ ...editForm, client_id: e.target.value })}
              >
                <option value="">— None —</option>
                {clients.map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </div>
            <div className="full">
              <label className="field">Employees & default % rule</label>
              <div className="member-edit-list">
                {employees.map((e) => {
                  const m = editForm.members[e.id] || { included: false, percentage: "" };
                  return (
                    <div key={e.id} className="member-edit-row">
                      <label className="check-row" style={{ flex: 1 }}>
                        <input
                          type="checkbox"
                          className="cbx"
                          checked={!!m.included}
                          onChange={() => toggleEmp(e.id)}
                        />
                        <span style={{ fontWeight: 500 }}>{e.name}</span>
                      </label>
                      <div className="pct-input-wrap">
                        <input
                          type="number"
                          min="0"
                          max="100"
                          step="0.1"
                          placeholder="—"
                          value={m.percentage}
                          onChange={(ev) => setEmpPct(e.id, ev.target.value)}
                          disabled={!m.included}
                          title="Default % of client price per assigned task (one decimal allowed)"
                        />
                        <span className="pct-suffix">%</span>
                      </div>
                    </div>
                  );
                })}
                {employees.length === 0 && <span className="muted-sm">No employees yet.</span>}
              </div>
            </div>
            <div className="full" style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
              <button type="button" onClick={() => setEditing(false)}>Cancel</button>
              <button type="submit" className="primary">Save changes</button>
            </div>
          </div>
        </form>
      )}

      {user?.role === "admin" && (
        <PaySplitPanel
          projectId={projectId}
          reloadKey={sheets.length}
          title="Outstanding pay split"
        />
      )}

      <div className="section-head">
        <h3>Milestones</h3>
        {user?.role === "admin" && googleConnected && (
          <div className="row-h" style={{ gap: 10, alignItems: "center" }}>
            {syncAllMsg && <span className="check-msg">{syncAllMsg}</span>}
            <button
              type="button"
              className="ghost"
              onClick={syncAllSheets}
              disabled={syncingAll || sheets.length === 0}
              title="Give every milestone, past ones included, its own tab in this project's Google Sheet"
            >
              {syncingAll ? "Syncing…" : "Sync all pages to Google"}
            </button>
          </div>
        )}
      </div>

      {user?.role === "admin" && (
        <>
          <form className="inline-form" onSubmit={addSheet} style={{ marginBottom: 6 }}>
            <input
              placeholder={resolvedName}
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
            />
            <button className="primary" type="submit">Add milestone</button>
          </form>
          <div className="muted-sm" style={{ marginBottom: 16, paddingLeft: 4 }}>
            Today's date is appended automatically. Type a date like
            {" "}<code style={{ fontFamily: "var(--font-mono)" }}>10-05-2026</code>{" "}
            at the end of the label to override it.
          </div>
        </>
      )}

      {sheets.length === 0 ? (
        <div className="list">
          <div className="empty">
            No milestones yet.
            <small>{user?.role === "admin" ? "Add your first milestone above." : "Milestones will appear here once added."}</small>
          </div>
        </div>
      ) : (
        <SheetSections
          sheets={sheets}
          user={user}
          navigate={navigate}
          deleteSheet={requestDeleteSheet}
          showPast={showPast}
          setShowPast={setShowPast}
        />
      )}

      <ConfirmTypeModal
        open={deleteSheetId !== null}
        title="Delete this milestone?"
        description="The milestone and all of its tasks will be permanently deleted. This cannot be undone."
        phrase="delete this milestone"
        confirmLabel="Delete milestone"
        danger
        onConfirm={confirmDeleteSheet}
        onCancel={() => setDeleteSheetId(null)}
      />
    </>
  );
}
